/**
 * Case page: "Propose a source" (W3-02).
 *
 * An officer who meets a useful data source while working a case files it here
 * (`POST /cases/{case_id}/source-candidates`): the URL, what it showed, the case
 * fact or evidence item that shows it, and what they know about its terms. The
 * candidate then goes through review (Settings → Sources); nothing is harvested
 * until it has been reviewed, approved, built and trial-run
 * (docs/SOURCE_ONBOARDING_LIFECYCLE.md).
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Globe, Loader2 } from 'lucide-react';
import { sourcesService, type SourceCandidate } from '../../services/api';
import { Button } from '../ui/button';

const EMPTY = { url: '', what_it_showed: '', evidence_ref: '', evidence_sha256: '', legal_note: '' };

function errorText(err: unknown): string {
    const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
    const detail = e?.response?.data?.detail as { message?: string; code?: string } | string | undefined;
    if (e?.response?.status === 403) return 'You do not have access to propose sources for this case.';
    if (detail && typeof detail === 'object') return detail.message || detail.code || 'Request failed';
    if (typeof detail === 'string') return detail;
    return e?.message || 'Request failed';
}

export const ProposeSourceCard: React.FC<{ caseId: string }> = ({ caseId }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filed, setFiled] = useState<SourceCandidate | null>(null);

    const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const urlOk = /^https?:\/\/\S+$/i.test(form.url.trim());
    const shaOk = !form.evidence_sha256.trim() || /^[0-9a-fA-F]{64}$/.test(form.evidence_sha256.trim());
    const canSubmit = urlOk && shaOk && form.what_it_showed.trim().length > 0 && !busy;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const body = {
                url: form.url.trim(),
                what_it_showed: form.what_it_showed.trim(),
                ...(form.evidence_ref.trim() ? { evidence_ref: form.evidence_ref.trim() } : {}),
                ...(form.evidence_sha256.trim() ? { evidence_sha256: form.evidence_sha256.trim() } : {}),
                ...(form.legal_note.trim() ? { legal_note: form.legal_note.trim() } : {}),
            };
            const created = await sourcesService.propose(caseId, body);
            setFiled(created);
            setForm(EMPTY);
            setOpen(false);
            toast.success(created.open_duplicates
                ? `Source proposed. ${created.open_duplicates} other open proposal(s) name the same URL.`
                : 'Source proposed for review.');
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="propose-source-card">
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    <h3 className="font-medium">Data sources</h3>
                </div>
                {!open && (
                    <Button size="sm" variant="outline" onClick={() => { setOpen(true); setError(null); }}>
                        Propose a source
                    </Button>
                )}
            </div>
            {!open && (
                <p className="p-4 text-xs text-muted-foreground">
                    Found a site, list or API in this case that the platform should collect from? Propose it for review.
                    {filed && <span className="block mt-1 text-foreground" data-testid="propose-source-filed">
                        Filed: <span className="font-mono break-all">{filed.url}</span> (state: {filed.status}).
                    </span>}
                </p>
            )}
            {open && (
                <form className="p-4 space-y-3" onSubmit={submit} aria-label="Propose a source">
                    <div className="space-y-1">
                        <label htmlFor="ps-url" className="block text-sm font-medium">URL or API endpoint *</label>
                        <input id="ps-url" value={form.url} onChange={set('url')} placeholder="https://"
                            className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm" />
                        {form.url.trim() && !urlOk && <p className="text-xs text-red-600">Enter an http(s) URL.</p>}
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="ps-what" className="block text-sm font-medium">What it showed *</label>
                        <textarea id="ps-what" value={form.what_it_showed} onChange={set('what_it_showed')} rows={3}
                            placeholder="What you saw there and which question of the case it answers"
                            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm" />
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="ps-ref" className="block text-sm font-medium">Case fact or evidence item</label>
                        <input id="ps-ref" value={form.evidence_ref} onChange={set('evidence_ref')}
                            placeholder="The evidence item, note or node that shows it"
                            className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="ps-sha" className="block text-sm font-medium">SHA-256 of the captured page (optional)</label>
                        <input id="ps-sha" value={form.evidence_sha256} onChange={set('evidence_sha256')}
                            className="h-8 w-full rounded-md border border-input bg-background px-2 font-mono text-xs" />
                        {!shaOk && <p className="text-xs text-red-600">A SHA-256 is 64 hex characters.</p>}
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="ps-legal" className="block text-sm font-medium">Legal note</label>
                        <textarea id="ps-legal" value={form.legal_note} onChange={set('legal_note')} rows={2}
                            placeholder="Terms of use, login needed, public register, unknown"
                            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm" />
                    </div>
                    {error && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
                    <div className="flex items-center gap-2">
                        <Button type="submit" size="sm" disabled={!canSubmit}>
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Submit for review
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => { setOpen(false); setError(null); }}>
                            Cancel
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default ProposeSourceCard;
